import { useCallback, useMemo, useState } from "react";

export type FieldError = string;

export type DraftValidator<T> = (draft: T) => Partial<Record<keyof T, FieldError>>;

export interface UseDraftEditOptions<T extends Record<string, any>> {
  initial: T;
  validate?: DraftValidator<T>;
  onPublish: (changes: Partial<T>, draft: T) => Promise<void>;
  labels?: Partial<Record<keyof T, string>>;
  format?: Partial<Record<keyof T, (v: any) => string>>;
}

export interface DraftChange {
  key: string;
  label: string;
  from: string;
  to: string;
}

export function useDraftEdit<T extends Record<string, any>>(opts: UseDraftEditOptions<T>) {
  const { initial, validate, onPublish, labels = {}, format = {} } = opts;
  const [draft, setDraft] = useState<T>(initial);
  const [publishing, setPublishing] = useState(false);

  const fmt = useCallback(
    (k: keyof T, v: any) => {
      const f = (format as any)[k];
      if (f) return f(v);
      if (v === null || v === undefined || v === "") return "—";
      return String(v);
    },
    [format],
  );

  const set = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  }, []);

  const reset = useCallback(
    (next?: T) => {
      setDraft(next ?? initial);
    },
    [initial],
  );

  const changes = useMemo<DraftChange[]>(() => {
    const out: DraftChange[] = [];
    for (const k of Object.keys(draft) as (keyof T)[]) {
      if (!Object.is(draft[k], initial[k])) {
        out.push({
          key: String(k),
          label: (labels as any)[k] ?? String(k),
          from: fmt(k, initial[k]),
          to: fmt(k, draft[k]),
        });
      }
    }
    return out;
  }, [draft, initial, labels, fmt]);

  const errors = useMemo(() => (validate ? validate(draft) : {}), [draft, validate]);
  const errorList = useMemo(
    () =>
      Object.entries(errors)
        .filter(([, v]) => !!v)
        .map(([k, v]) => ({
          key: k,
          label: (labels as any)[k] ?? k,
          message: v as string,
        })),
    [errors, labels],
  );

  const isDirty = changes.length > 0;
  const canPublish = isDirty && errorList.length === 0 && !publishing;

  const publish = useCallback(async () => {
    if (!canPublish) return;
    setPublishing(true);
    try {
      const patch: Partial<T> = {};
      for (const c of changes) (patch as any)[c.key] = (draft as any)[c.key];
      await onPublish(patch, draft);
    } finally {
      setPublishing(false);
    }
  }, [canPublish, changes, draft, onPublish]);

  return {
    draft,
    set,
    reset,
    changes,
    errors: errorList,
    isDirty,
    canPublish,
    publishing,
    publish,
  } as const;
}
